/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  Fragment,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import styles from "./MMScreenInputViews.module.css";
import resetBtn from "../../../../../assets/images/resetBtn.png";
import Upload from "../../../../../assets/images/upload_icon.svg";
import Plus from "../../../../../assets/images/Plus.svg";
import Excel from "../../../../../assets/images/Excel.svg";
import {
  CircularProgress,
  TableContainer,
  Table,
  ListItem,
} from "@mui/material";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import { useDailyPhasingContext } from "../../../../../utils/dailyphaisngContext/DailyphasingContext";
import CustomButton from "../../../../components/buttons/CustomButton";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import TblHeader from "./TblHeader";
import TblBody from "./TblBody";
import TblFooter from "./TblFooter";
import ReasonForChange from "../../../../components/drawers/ReasonForChange";
import AddNewCBU from "../../../../components/drawers/AddNewCBU.js";
import { useInfiniteScrollData } from "Samarth Module/pages/dailyPhasing/hooks/useInfiniteScrollData";
import { debounce } from "lodash";
import {
  delete_cbu_line_level_data,
  getValidatedNewCBUFromExcel,
} from "Samarth Module/api/DailyphaingAPIs";
import { smartProdSchedularInputFields } from "Samarth Module/constants/dailyPhasing";
import DuplicateRowPopup from "Samarth Module/components/popup/duplicateRowPopup";
import { hasS1Flag } from "../../../../../utils/mmScreenInput";
import { readExcelFileToJson } from "utils/excelUtils";

// 1. detect changes where Line or Cascade is modified
function getChangedRowsLineCascade(excelRows, globalRows) {
  // ✅ Create map by id for direct matching
  const globalMap = new Map(globalRows.map((r) => [r.id, r]));
  const changed = [];

  excelRows.forEach((excelRow) => {
    // ✅ Skip if no id (new row)
    if (!excelRow.id || excelRow.id === "") return;

    const match = globalMap.get(excelRow.id);

    if (match) {
      // check if Line or Cascade is different
      if (
        String(match["Line"] ?? "").trim() !==
          String(excelRow["Line"] ?? "").trim() ||
        String(match["Cascade"] ?? "").trim() !==
          String(excelRow["Cascade"] ?? "").trim()
      ) {
        // Check if Flag is M2 - need to create TWO rows
        if (match["Flag"] === "M2") {
          const timestamp = Date.now();

          // OLD ROW: Keep old Cascade/Line, Mon-Wed from Excel, Thu-Sun = 0
          const oldRow = {
            ...match,
            id: match.id, // Keep original id
            Cascade: match["Cascade"], // Old Cascade
            Line: match["Line"], // Old Line
            PreCascade: match["PreCascade"] || match["Cascade"],
            PreLine: match["PreLine"] || match["Line"],
            Mon: excelRow["Mon"] ?? match["Mon"],
            Tue: excelRow["Tue"] ?? match["Tue"],
            Wed: excelRow["Wed"] ?? match["Wed"],
            Thu: 0,
            Fri: 0,
            Sat: 0,
            Sun: 0,
            "Reason for change": match["Reason for change"],
            Week: match["Week"],
            Flag: match["Flag"],
            isUpdated: true,
            constValues: {
              ...match.constValues,
              Cascade: match["Cascade"],
              Line: match["Line"],
              Mon: excelRow["Mon"] ?? match["Mon"],
              Tue: excelRow["Tue"] ?? match["Tue"],
              Wed: excelRow["Wed"] ?? match["Wed"],
              Thu: 0,
              Fri: 0,
              Sat: 0,
              Sun: 0,
            },
          };

          // NEW ROW: New Cascade/Line, Mon-Wed = 0, Thu-Sun original values
          const newRow = {
            ...match,
            id: `DUP_${timestamp}_${match.id}`, // New unique id
            Cascade: excelRow["Cascade"], // New Cascade
            Line: excelRow["Line"], // New Line
            PreCascade: match["Cascade"], // Old Cascade as PreCascade
            PreLine: match["Line"], // Old Line as PreLine
            Mon: 0,
            Tue: 0,
            Wed: 0,
            Thu: excelRow["Thu"] ?? match["Thu"],
            Fri: excelRow["Fri"] ?? match["Fri"],
            Sat: excelRow["Sat"] ?? match["Sat"],
            Sun: excelRow["Sun"] ?? match["Sun"],
            "Reason for change": excelRow["Reason for change"],
            Week: match["Week"],
            Flag: match["Flag"],
            IsDuplicateRow: true,
            isUpdated: true,
            constValues: {
              ...match.constValues,
              Cascade: excelRow["Cascade"],
              Line: excelRow["Line"],
              Mon: 0,
              Tue: 0,
              Wed: 0,
              Thu: excelRow["Thu"] ?? match["Thu"],
              Fri: excelRow["Fri"] ?? match["Fri"],
              Sat: excelRow["Sat"] ?? match["Sat"],
              Sun: excelRow["Sun"] ?? match["Sun"],
            },
          };

          changed.push(oldRow, newRow);
        } else {
          // For M1 or other flags, keep existing logic
          const updatedRow = {
            ...match,
            id: match.id,
            Line: excelRow["Line"],
            Cascade: excelRow["Cascade"],
            Mon: excelRow["Mon"] ?? match["Mon"],
            Tue: excelRow["Tue"] ?? match["Tue"],
            Wed: excelRow["Wed"] ?? match["Wed"],
            Thu: excelRow["Thu"] ?? match["Thu"],
            Fri: excelRow["Fri"] ?? match["Fri"],
            Sat: excelRow["Sat"] ?? match["Sat"],
            Sun: excelRow["Sun"] ?? match["Sun"],
            "Reason for change": excelRow["Reason for change"],
            PreCascade: match["Cascade"],
            PreLine: match["Line"],
            Week: match["Week"],
            Flag: match["Flag"],
          };
          changed.push(updatedRow);
        }
      }
    }
  });

  return changed.map((item) => ({
    ...item,
    Status: "Change in Line or Cascade",
  }));
}

const MMInputView = ({ username, exportToExcel }) => {
  const {
    setTableData,
    editableTableData,
    editableTableFilters,
    editableTableFooter,
    setEditableTableFilters,
    editableTableDataColumns,
    filteredEditableTableData,
    setFilteredEditableTableData,
    setEditableTableData,
    addedRows,
    setAddedRows,
    globalFilters,
    saveComments,
    setBtnDisable,
    cascadeLineList,
    duplicateRows,
    setDuplicateRows,
    selectedWeek,
    // setThresholdBlocker
  } = useDailyPhasingContext();
  const { data, setData, containerRef } = useInfiniteScrollData(
    filteredEditableTableData,
    30,
  );
  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    IsAddNewCBU: false,
    rowIndex: null,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: "",
    type: "error",
  });
  const [duplicateEntryPopup, setDuplicateEntryPopup] = useState({
    value: false,
    message: [],
  });
  const [filteredAddedRows, setFilteredAddedRows] = useState([]);
  const [filteredDuplicateRows, setFilteredDuplicateRows] = useState([]);
  // Track filter changes separately to prevent unnecessary re-filtering
  const [lastAppliedFilters, setLastAppliedFilters] = useState([]);

  const globalFilteredLineCascadeOptions = useMemo(() => {
    const activeGlobalFilters = globalFilters.filter(
      (filter) =>
        Array.isArray(filter.selectedData) && filter.selectedData.length > 0,
    );
    if (!activeGlobalFilters.length) return null;

    const rowsForSelectedFilters = [
      ...editableTableData,
      ...addedRows,
      ...duplicateRows,
    ].filter((row) =>
      activeGlobalFilters.every((filter) =>
        filter.selectedData.includes(row[filter.label]),
      ),
    );

    return ["Cascade", "Line"].reduce(
      (options, label) => ({
        ...options,
        [label]: Array.from(
          new Set(rowsForSelectedFilters.map((row) => row[label])),
        )
          .filter(
            (value) => value !== undefined && value !== null && value !== "",
          )
          .sort((first, second) => String(first).localeCompare(String(second))),
      }),
      {},
    );
  }, [globalFilters, editableTableData, addedRows, duplicateRows]);

  const addNewCBUDrawerToggle = useCallback(() => {
    setIsDrawerOpen((prev) => !prev);
  }, []);

  const toggleDrawer = useCallback((isOpen, IsAddNewCBU, rowIndex = null) => {
    setDrawerState((prev) => ({
      ...prev,
      isOpen,
      IsAddNewCBU,
      rowIndex,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setEditableTableFilters((filters) =>
      filters.map((filter) => ({ ...filter, selectedData: [] })),
    );
  }, [setEditableTableFilters]);

  const handleFilterChange = useCallback(
    (label, values) => {
      setEditableTableFilters((prevFilters) => {
        if (!isLineCascadeField(label)) {
          return prevFilters.map((filter) =>
            filter.label === label
              ? { ...filter, selectedData: values }
              : filter,
          );
        }

        const pairedLabel = getPairedLineCascadeField(label);

        const allowedPairedOptions =
          globalFilteredLineCascadeOptions?.[pairedLabel] ?? [];

        const validPairedValues = new Set(
          cascadeLineList
            .filter((item) => values.includes(item[label]))
            .map((item) => item[pairedLabel])
            .filter(
              (value) =>
                allowedPairedOptions.length === 0 ||
                allowedPairedOptions.includes(value),
            ),
        );

        return prevFilters.map((filter) => {
          if (filter.label === label) {
            return {
              ...filter,
              selectedData: values,
            };
          }

          if (filter.label === pairedLabel && values.length > 0) {
            return {
              ...filter,
              selectedData: filter.selectedData.filter((value) =>
                validPairedValues.has(value),
              ),
            };
          }

          return filter;
        });
      });
    },
    [
      cascadeLineList,
      globalFilteredLineCascadeOptions,
      setEditableTableFilters,
    ],
  );
  const getFilterOptions = useCallback(
    (filter) => {
      if (!isLineCascadeField(filter.label)) {
        return filter.data;
      }

      const baseOptions =
        globalFilteredLineCascadeOptions?.[filter.label] ?? filter.data ?? [];

      const pairedLabel = getPairedLineCascadeField(filter.label);

      const pairedValues =
        editableTableFilters.find((item) => item.label === pairedLabel)
          ?.selectedData ?? [];

      if (pairedValues.length === 0) {
        return baseOptions;
      }

      const linkedOptions = new Set(
        cascadeLineList
          .filter((item) => pairedValues.includes(item[pairedLabel]))
          .map((item) => item[filter.label])
          .filter(
            (value) => value !== undefined && value !== null && value !== "",
          ),
      );

      return baseOptions.filter((value) => linkedOptions.has(value));
    },
    [cascadeLineList, editableTableFilters, globalFilteredLineCascadeOptions],
  );
  const closeSnackbar = useCallback(() => {
    setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
  }, []);

  const getStatusValue = (fieldLabel) => {
    if (smartProdSchedularInputFields.includes(fieldLabel)) {
      return "Change in the phasing plan for any CBU";
    }
    if (fieldLabel === "Line" || fieldLabel === "Cascade") {
      return "Change in Line or Cascade";
    }
    return undefined;
  };

  const isLineCascadeField = (fieldLabel) =>
    fieldLabel === "Cascade" || fieldLabel === "Line";

  const getPairedLineCascadeField = (fieldLabel) =>
    fieldLabel === "Cascade" ? "Line" : "Cascade";

  const getLineCascadeMapping = (fieldLabel, value) => {
    const mapping = { Line: [], Cascade: [] };
    if (!isLineCascadeField(fieldLabel)) return mapping;

    const selectedLabel = getPairedLineCascadeField(fieldLabel);
    mapping[selectedLabel] = cascadeLineList
      .map((item) => {
        return item[fieldLabel] === value ? item[selectedLabel] : null;
      })
      .filter(Boolean);
    return mapping;
  };

  const getMappedFieldValue = (mappedValues, fallbackValue) => {
    if (mappedValues.length === 1) return mappedValues[0];
    return fallbackValue;
  };

  const resolveLineCascadeValue = (
    fieldName,
    fieldLabel,
    value,
    currentRow,
    lineCascadeMapping,
  ) => {
    if (fieldLabel === fieldName) return value;
    return getMappedFieldValue(
      lineCascadeMapping[fieldName],
      currentRow[fieldName],
    );
  };

  const getLineCascadeOptions = (
    lineCascadeMapping,
    currentRow,
    fieldName,
    optionName,
  ) => {
    if (lineCascadeMapping[fieldName].length > 0)
      return [...lineCascadeMapping[fieldName]];
    return [...(currentRow[optionName] || [])];
  };

  const createM2OldRow = (currentRow) =>
    structuredClone({
      ...currentRow,
      Mon: currentRow["Mon"],
      Tue: currentRow["Tue"],
      Wed: currentRow["Wed"],
      Thu: 0,
      Fri: 0,
      Sat: 0,
      Sun: 0,
      isUpdated: true,
      Status: "Change in Line or Cascade",
      constValues: {
        ...currentRow.constValues,
        Thu: 0,
        Fri: 0,
        Sat: 0,
        Sun: 0,
      },
    });

  const createM2NewRow = (
    currentRow,
    id,
    fieldLabel,
    value,
    lineCascadeMapping,
  ) => {
    const timestamp = Date.now();
    const cascadeValue = resolveLineCascadeValue(
      "Cascade",
      fieldLabel,
      value,
      currentRow,
      lineCascadeMapping,
    );
    const lineValue = resolveLineCascadeValue(
      "Line",
      fieldLabel,
      value,
      currentRow,
      lineCascadeMapping,
    );

    return structuredClone({
      ...currentRow,
      id: `DUP_${timestamp}_${id}`,
      Cascade: cascadeValue,
      Line: lineValue,
      PreCascade: currentRow["Cascade"],
      PreLine: currentRow["Line"],
      Mon: 0,
      Tue: 0,
      Wed: 0,
      Thu: currentRow["Thu"],
      Fri: currentRow["Fri"],
      Sat: currentRow["Sat"],
      Sun: currentRow["Sun"],
      IsDuplicateRow: true,
      isUpdated: true,
      Status: "Change in Line or Cascade",
      cascadeOptions: getLineCascadeOptions(
        lineCascadeMapping,
        currentRow,
        "Cascade",
        "cascadeOptions",
      ),
      lineOptions: getLineCascadeOptions(
        lineCascadeMapping,
        currentRow,
        "Line",
        "lineOptions",
      ),
      constValues: {
        ...currentRow.constValues,
        Cascade: cascadeValue,
        Line: lineValue,
        Mon: 0,
        Tue: 0,
        Wed: 0,
        Thu: currentRow["Thu"],
        Fri: currentRow["Fri"],
        Sat: currentRow["Sat"],
        Sun: currentRow["Sun"],
      },
    });
  };

  const appendRowIfMissing = (rows, rowToAdd) => {
    if (rows.some((row) => row.id === rowToAdd.id)) return rows;
    return [...rows, rowToAdd];
  };

  const updateDisplayDataWithDuplicate = (displayData, id, oldRow, newRow) => {
    const displayIndex = displayData.findIndex((row) => row.id === id);
    if (displayIndex === -1) return displayData;

    const updatedDisplayData = [...displayData];
    updatedDisplayData[displayIndex] = structuredClone(oldRow);
    if (!displayData.some((row) => row.id === newRow.id)) {
      updatedDisplayData.splice(displayIndex + 1, 0, structuredClone(newRow));
    }
    return updatedDisplayData;
  };

  const applyM2LineCascadeChange = (
    prevData,
    index,
    currentRow,
    id,
    fieldLabel,
    value,
    lineCascadeMapping,
  ) => {
    const oldRow = createM2OldRow(currentRow);
    const newRow = createM2NewRow(
      currentRow,
      id,
      fieldLabel,
      value,
      lineCascadeMapping,
    );
    const updatedData = [...prevData];
    updatedData[index] = oldRow;

    if (!updatedData.some((row) => row.id === newRow.id)) {
      updatedData.splice(index + 1, 0, newRow);
      setDuplicateRows((prev) => appendRowIfMissing(prev, newRow));
    }

    setData((prevDisplayData) =>
      updateDisplayDataWithDuplicate(prevDisplayData, id, oldRow, newRow),
    );
    debouncedChangeHandler(id, oldRow, fieldLabel);
    return updatedData;
  };

  const createLineCascadeUpdatedRow = (
    currentRow,
    fieldLabel,
    value,
    statusValue,
    lineCascadeMapping,
  ) => {
    const mappedField = getPairedLineCascadeField(fieldLabel);
    const mappedFieldValue = getMappedFieldValue(
      lineCascadeMapping[mappedField],
      currentRow[mappedField],
    );

    return structuredClone({
      ...currentRow,
      [fieldLabel]: value,
      isUpdated: true,
      ...(statusValue && { Status: statusValue }),
      cascadeOptions: getLineCascadeOptions(
        lineCascadeMapping,
        currentRow,
        "Cascade",
        "cascadeOptions",
      ),
      lineOptions: getLineCascadeOptions(
        lineCascadeMapping,
        currentRow,
        "Line",
        "lineOptions",
      ),
      [mappedField]: mappedFieldValue,
    });
  };

  const createDuplicateUpdatedRow = (
    currentRow,
    fieldLabel,
    value,
    statusValue,
    lineCascadeMapping,
  ) => {
    const mappedField = getPairedLineCascadeField(fieldLabel);
    const mappedFieldValue = getMappedFieldValue(
      lineCascadeMapping[mappedField],
      currentRow[mappedField],
    );

    return structuredClone({
      ...currentRow,
      [fieldLabel]: value,
      isUpdated: true,
      ...(statusValue && { Status: statusValue }),
      cascadeOptions: [...(lineCascadeMapping["Cascade"] || [])],
      lineOptions: [...(lineCascadeMapping["Line"] || [])],
      [mappedField]: mappedFieldValue,
      constValues: {
        ...currentRow.constValues,
        [fieldLabel]: value,
      },
    });
  };

  const updateAddedRowsForInput = (
    rows,
    id,
    fieldLabel,
    value,
    statusValue,
  ) => {
    const index = rows.findIndex((row) => row.id === id);
    if (index === -1) return rows;

    const updatedRow = {
      ...rows[index],
      [fieldLabel]: value,
      isUpdated: true,
      ...(statusValue && { Status: statusValue }),
    };
    const updatedData = [...rows];
    updatedData[index] = updatedRow;
    debouncedCascadeAndLineCheck(id, updatedRow, editableTableData);
    return updatedData;
  };

  const shouldSkipDisplayUpdate = (currentRow, fieldLabel, value) => {
    return (
      currentRow["Flag"] === "M2" &&
      isLineCascadeField(fieldLabel) &&
      currentRow[fieldLabel] !== value
    );
  };

  const updateEditableRowsForInput = (
    rows,
    id,
    fieldLabel,
    value,
    statusValue,
    lineCascadeMapping,
  ) => {
    const index = rows.findIndex((row) => row.id === id);
    if (index === -1) return rows;

    const currentRow = rows[index];
    if (shouldSkipDisplayUpdate(currentRow, fieldLabel, value)) {
      return applyM2LineCascadeChange(
        rows,
        index,
        currentRow,
        id,
        fieldLabel,
        value,
        lineCascadeMapping,
      );
    }

    const updatedRow = createLineCascadeUpdatedRow(
      currentRow,
      fieldLabel,
      value,
      statusValue,
      lineCascadeMapping,
    );
    const updatedData = [...rows];
    updatedData[index] = updatedRow;
    debouncedChangeHandler(id, updatedRow, fieldLabel);
    return updatedData;
  };

  const updateDisplayRowsForInput = (
    rows,
    id,
    fieldLabel,
    value,
    statusValue,
    lineCascadeMapping,
  ) => {
    const index = rows.findIndex((row) => row.id === id);
    if (index === -1) return rows;

    const currentRow = rows[index];
    if (shouldSkipDisplayUpdate(currentRow, fieldLabel, value)) return rows;

    const updatedRow = createLineCascadeUpdatedRow(
      currentRow,
      fieldLabel,
      value,
      statusValue,
      lineCascadeMapping,
    );
    const updatedData = [...rows];
    updatedData[index] = updatedRow;
    return updatedData;
  };

  const updateDuplicateRowsForInput = (
    rows,
    fieldLabel,
    value,
    statusValue,
    lineCascadeMapping,
    id,
  ) => {
    const index = rows.findIndex((row) => row.id === id);
    if (index === -1) return rows;

    const updatedRow = createDuplicateUpdatedRow(
      rows[index],
      fieldLabel,
      value,
      statusValue,
      lineCascadeMapping,
    );
    const updatedData = [...rows];
    updatedData[index] = updatedRow;
    return updatedData;
  };

  // Apply filters to all rows (editableTableData + addedRows + duplicateRows)
  useEffect(() => {
    // Check if filters actually changed
    const filtersChanged =
      JSON.stringify(lastAppliedFilters) !==
      JSON.stringify(editableTableFilters);

    // Check if current data is M2 (check first row's Flag)
    const isM2Data =
      editableTableData.length > 0 && editableTableData[0]["Flag"] === "M2";

    // Get active filters
    const activeFilters = editableTableFilters.filter(
      (f) => f.selectedData && f.selectedData.length > 0,
    );

    if (activeFilters.length === 0) {
      // No filters applied, show all rows
      if (filtersChanged) {
        setFilteredEditableTableData(editableTableData);
        setLastAppliedFilters(editableTableFilters);
      }
      setFilteredAddedRows(addedRows);
      // Only set duplicate rows for M2 data
      setFilteredDuplicateRows(isM2Data ? duplicateRows : []);
      return;
    }

    // Only re-filter editableTableData if filters changed
    if (filtersChanged) {
      // Apply filters to editableTableData
      const filteredEditable = editableTableData.filter((row) => {
        return activeFilters.every((filter) => {
          const rowValue = row[filter.label];
          // Handle undefined or null values
          if (rowValue === undefined || rowValue === null) return false;
          return filter.selectedData.includes(rowValue);
        });
      });
      setFilteredEditableTableData(filteredEditable);
      setLastAppliedFilters(editableTableFilters);
    }

    // Always re-filter addedRows and duplicateRows when they change
    const filteredAdded = addedRows.filter((row) => {
      return activeFilters.every((filter) => {
        const rowValue = row[filter.label];
        // Handle undefined or null values
        if (rowValue === undefined || rowValue === null) return false;
        return filter.selectedData.includes(rowValue);
      });
    });

    // Apply filters to duplicateRows (only for M2 data)
    const filteredDuplicates = isM2Data
      ? duplicateRows.filter((row) => {
          return activeFilters.every((filter) => {
            const rowValue = row[filter.label];
            // Handle undefined or null values
            if (rowValue === undefined || rowValue === null) return false;
            return filter.selectedData.includes(rowValue);
          });
        })
      : [];

    setFilteredAddedRows(filteredAdded);
    setFilteredDuplicateRows(filteredDuplicates);
  }, [
    editableTableFilters,
    editableTableData,
    addedRows,
    duplicateRows,
    setFilteredEditableTableData,
    lastAppliedFilters,
  ]);

  const checkCascadeAndLine = useCallback((id, updatedRow, allData, label) => {
    try {
      const IsDuplicate = allData.some(
        (dt) =>
          dt.id !== id && // exclude current row by comparing row IDs
          dt["CBU code"] === updatedRow["CBU code"] &&
          dt["Site code"] === updatedRow["Site code"] &&
          dt["Format"] === updatedRow["Format"] &&
          dt["Cascade"] === updatedRow["Cascade"] &&
          dt["Line"] === updatedRow["Line"] &&
          dt["Site cluster"] === updatedRow["Site cluster"],
      );

      if (IsDuplicate) {
        // Show error only when Cascade or Line is changed
        if (label === "Cascade" || label === "Line") {
          const error = `The combination of Cascade '${updatedRow["Cascade"]}' and Line '${updatedRow["Line"]}' is already in use for Site code '${updatedRow["Site code"]}' and CBU '${updatedRow["CBU code"]}'. Please enter a unique combination.`;
          setSnackBarObject({
            snackbarOpen: true,
            message: error,
            type: "error",
          });
        }
        // Always disable button if duplicate exists
        setBtnDisable(true);
      } else {
        setBtnDisable(false);
      }
    } catch (error) {
      setSnackBarObject({
        snackbarOpen: true,
        message: error.message,
        type: "error",
      });
    }
  }, []);

  const debouncedCascadeAndLineCheck = useMemo(
    () =>
      debounce((id, updatedRow, allData, label) => {
        checkCascadeAndLine(id, updatedRow, allData, label);
      }, 400),
    [checkCascadeAndLine],
  );

  const changeHandler = useCallback(
    (id, updatedRow, label) => {
      setEditableTableData((prevData) => {
        const index = prevData.findIndex((row) => row.id === id);
        if (index === -1) return prevData;

        // Deep clone the updated row to prevent reference issues
        const clonedRow = structuredClone(updatedRow);
        const updatedData = [...prevData];
        updatedData[index] = clonedRow;
        debouncedCascadeAndLineCheck(id, clonedRow, updatedData, label);
        return updatedData;
      });
    },
    [debouncedCascadeAndLineCheck],
  );

  const debouncedChangeHandler = useCallback(
    debounce((id, updatedRow, label) => {
      changeHandler(id, updatedRow, label);
    }, 500),
    [changeHandler],
  );

  const handleInputChange = useCallback(
    (id, label, IsAddNewCBU, value) => {
      if (label === "Reason for change" && value === "Others") {
        toggleDrawer(true, IsAddNewCBU, id);
        return;
      }

      const statusValue = getStatusValue(label);

      if (IsAddNewCBU) {
        setAddedRows((prev) =>
          updateAddedRowsForInput(prev, id, label, value, statusValue),
        );
      } else {
        const line_cascade_mapping = getLineCascadeMapping(label, value);

        setEditableTableData((prevData) =>
          updateEditableRowsForInput(
            prevData,
            id,
            label,
            value,
            statusValue,
            line_cascade_mapping,
          ),
        );
        setData((prevData) =>
          updateDisplayRowsForInput(
            prevData,
            id,
            label,
            value,
            statusValue,
            line_cascade_mapping,
          ),
        );
        setDuplicateRows((prevData) =>
          updateDuplicateRowsForInput(
            prevData,
            label,
            value,
            statusValue,
            line_cascade_mapping,
            id,
          ),
        );
      }
    },
    [
      editableTableData,
      cascadeLineList,
      debouncedChangeHandler,
      setDuplicateRows,
      setData,
      setFilteredEditableTableData,
    ],
  );

  const saveCommentInResponseChange = useCallback(
    (comment, rowId, IsAddNewCBU) => {
      if (IsAddNewCBU) {
        setAddedRows((prevData) => {
          const index = prevData.findIndex((row) => row.id === rowId);
          if (index === -1) return prevData;
          const updatedRow = {
            ...prevData[index],
            "Reason for change": `Others, ${comment}`,
            isUpdated: true,
          };
          const updatedData = [...prevData];
          updatedData[index] = updatedRow;
          return updatedData;
        });
      } else {
        setData((prevData) => {
          const index = prevData.findIndex((row) => row.id === rowId);
          if (index === -1) return prevData;
          const updatedRow = {
            ...prevData[index],
            "Reason for change": `Others, ${comment}`,
            isUpdated: true,
          };
          const updatedData = [...prevData];
          updatedData[index] = updatedRow;
          debouncedChangeHandler(rowId, updatedRow);
          return updatedData;
        });

        // Also update duplicateRows if it's a duplicate row
        setDuplicateRows((prevData) => {
          const index = prevData.findIndex((row) => row.id === rowId);
          if (index === -1) return prevData;
          const updatedRow = {
            ...prevData[index],
            "Reason for change": `Others, ${comment}`,
            isUpdated: true,
          };
          const updatedData = [...prevData];
          updatedData[index] = updatedRow;
          return updatedData;
        });
      }
      toggleDrawer(false);
    },
    [toggleDrawer, debouncedChangeHandler],
  );

  const cancelCommentInResponseChange = useCallback(
    () => toggleDrawer(false),
    [toggleDrawer],
  );

  const addNewCBUHandler = useCallback(
    (newRows) => {
      setAddedRows((prev) => [...prev, ...newRows]);
    },
    [editableTableData],
  );

  const rowResetHandler = useCallback((id, IsAddNewCBU) => {
    if (IsAddNewCBU) {
      // Reset rows from addedRows (including newly added CBUs and duplicates from + button)
      setAddedRows((prev) => {
        const index = prev.findIndex((row) => row.id === id);
        if (index === -1) return prev;

        const array = [...prev];
        array[index] = {
          ...array[index],
          ...array[index].constValues,
          isUpdated: false,
        };
        return array;
      });
    } else {
      // Reset rows from main data (existing rows and duplicate rows)
      setData((prev) => {
        const index = prev.findIndex((row) => row.id === id);
        if (index === -1) return prev;

        const array = [...prev];
        array[index] = {
          ...array[index],
          ...array[index].constValues,
          isUpdated: false,
        };
        return array;
      });

      setFilteredEditableTableData((prev) => {
        const index = prev.findIndex((row) => row.id === id);
        if (index === -1) return prev;

        const array = [...prev];
        array[index] = {
          ...array[index],
          ...array[index].constValues,
          isUpdated: false,
        };
        return array;
      });

      setEditableTableData((prev) => {
        const index = prev.findIndex((row) => row.id === id);
        if (index === -1) return prev;

        const array = [...prev];
        array[index] = {
          ...array[index],
          ...array[index].constValues,
          isUpdated: false,
        };
        return array;
      });
    }
  }, []);

  const rowDeleteHandler = useCallback(
    async (id, IsAddNewCBU, row) => {
      setLoading(true);
      try {
        if (IsAddNewCBU) {
          // Remove from addedRows
          const array = [...addedRows];
          const index = array.findIndex((row) => row.id === id);

          if (index !== -1) {
            array.splice(index, 1);
            setAddedRows(array);
          }
        } else if (row.IsDuplicateRow) {
          // Remove duplicate row from all data arrays
          setDuplicateRows((prev) => prev.filter((item) => item.id !== id));

          setData((prev) => prev.filter((item) => item.id !== id));

          setFilteredEditableTableData((prev) =>
            prev.filter((item) => item.id !== id),
          );

          setEditableTableData((prev) => prev.filter((item) => item.id !== id));
        } else {
          // Call API to delete existing row from backend
          const payload = {
            email: username,
            Site_code: row["Site code"],
            CBU_code: row["CBU code"],
            Cascade: row["Cascade"],
            Line: row["Line"],
            PreCascade: row["PreCascade"],
            PreLine: row["PreLine"],
            Flag: row["Flag"],
            Week: +selectedWeek.replace("Week ", ""),
          };
          await delete_cbu_line_level_data(payload);
          setData((prev) => {
            const array = [...prev];
            array.splice(id, 1);
            return array;
          });
          setTableData((prev) => {
            const array = [...prev];
            return array.filter((item) => {
              return (
                item["Site code"] !== row["Site code"] ||
                item["CBU code"] !== row["CBU code"]
              );
            });
          });
          setFilteredEditableTableData((prev) => {
            const array = [...prev];
            array.splice(id, 1);
            return array;
          });
          setEditableTableData((prev) => {
            const array = [...prev];
            array.splice(id, 1);
            return array;
          });
        }
        setSnackBarObject({
          snackbarOpen: true,
          message: `Entry deleted successfully.`,
          type: "success",
        });
      } catch (error) {
        setSnackBarObject({
          snackbarOpen: true,
          message: error.message,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [addedRows],
  );

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !data.length) return data;

    return [...data].sort((a, b) => {
      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];

      if (!Number.isNaN(valueA) && !Number.isNaN(valueB)) {
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortConfig.direction === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return 0;
    });
  }, [data, sortConfig]);

  const handleExportToExcel = useCallback(() => {
    const data = [...filteredEditableTableData];
    const filteredEditableTableData1 = data.map((row) => {
      const exportRow = { ...row };
      delete exportRow["constValues"];
      exportRow["Line-CBU"] = row["Line"] + "-" + row["CBU code"];
      // ✅ Keep the id field for matching during upload
      exportRow["id"] = row["id"];
      return exportRow;
    });
    exportToExcel(filteredEditableTableData1, "CBU_line_table");
  }, [exportToExcel, filteredEditableTableData]);

  // validating the data before uploading
  const validateExportData = (data) => {
    if (!data || data.length === 0) {
      setSnackBarObject({
        snackbarOpen: true,
        message: "No data available to export.",
        type: "error",
      });
      return false;
    }

    if (data.length) {
      // Check for missing required fields
      const invalidRows = data.filter((row) => {
        return (
          row["Line"] === "" ||
          row["Cascade"] === "" ||
          row["Priority"] === "" ||
          row["Site code"] === "" ||
          row["CBU code"] === "" ||
          row["Site cluster"] === "" ||
          !row["Priority"].includes("Prio")
        );
      });

      if (invalidRows.length > 0) {
        setSnackBarObject({
          snackbarOpen: true,
          message:
            "Some rows have missing or invalid data. Please ensure all required fields are filled.",
          type: "error",
        });
        return false;
      }
    }

    return true;
  };

  const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());

  // days changed?
  const daysChanged = (oldRow, newRow) =>
    smartProdSchedularInputFields.some(
      (d) => norm(oldRow[d]) !== norm(newRow[d]),
    );

  const mergeDaysIntoOld = (oldRow, excelRow) => {
    const merged = { ...oldRow };
    smartProdSchedularInputFields.forEach((d) => {
      merged[d] = excelRow[d];
    });
    // keep these if you want to carry them, but they don't affect detection
    if ("Priority" in excelRow) merged.Priority = excelRow.Priority;
    if ("Reason for change" in excelRow)
      merged["Reason for change"] = excelRow["Reason for change"];
    // mark for UI if you like
    merged.isUpdated = true;
    return merged;
  };

  // returns only the rows where ONLY Mon–Sun changed (Line & Cascade same)
  const getChangedRowsDaysOnly = (excelRows, globalRows) => {
    // ✅ Create map by composite key: CBU code + Line + Cascade + Site code
    const globalMap = new Map(
      globalRows.map((r) => {
        const key = `${norm(r["CBU code"])}_${norm(r.Line)}_${norm(r.Cascade)}_${norm(r["Site code"])}`;
        return [key, r];
      }),
    );

    const changed = [];

    for (const excelRow of excelRows) {
      // Create composite key from excel row
      const key = `${norm(excelRow["CBU code"])}_${norm(excelRow.Line)}_${norm(excelRow.Cascade)}_${norm(excelRow["Site code"])}`;

      const oldRow = globalMap.get(key);
      if (!oldRow) continue; // not found in global → ignore (likely a new row)

      // Check if only days changed (Line & Cascade are already same due to key matching)
      if (daysChanged(oldRow, excelRow)) {
        changed.push(mergeDaysIntoOld(oldRow, excelRow));
      }
    }

    return changed.map((item) => ({
      ...item,
      Status: "Change in the phasing plan for any CBU",
    }));
  };

  // Helper: Check if row is new (no valid id)
  const isNewRow = (row) => {
    if (
      !("id" in row) ||
      row.id === undefined ||
      row.id === null ||
      row.id === ""
    ) {
      return true;
    }
    if (
      typeof row.id === "string" &&
      (row.id.startsWith("NEW_") || row.id.startsWith("DUP_"))
    ) {
      return false;
    }
    if (typeof row.id === "number") {
      return false;
    }
    return false;
  };

  // Helper: Check if row has valid id (edit candidate)
  const isEditCandidate = (row) => {
    return (
      "id" in row && row.id !== undefined && row.id !== null && row.id !== ""
    );
  };

  // Helper: Create day fields object for row updates
  const createDayFields = (updatedRow) => ({
    Mon: updatedRow["Mon"],
    Tue: updatedRow["Tue"],
    Wed: updatedRow["Wed"],
    Thu: updatedRow["Thu"],
    Fri: updatedRow["Fri"],
    Sat: updatedRow["Sat"],
    Sun: updatedRow["Sun"],
  });

  // Helper: Create updated row with all fields
  const createUpdatedRowData = (row, updated) => ({
    ...row,
    Line: updated["Line"],
    Cascade: updated["Cascade"],
    ...createDayFields(updated),
    Status: updated["Status"],
    "Reason for change": updated["Reason for change"],
    PreCascade: updated["PreCascade"],
    PreLine: updated["PreLine"],
    isUpdated: true,
  });

  // Helper: Map rows with regular edits
  const applyRegularEditsToRows = (dataArray, regularEdits) =>
    dataArray.map((row) => {
      const updated = regularEdits.find((r) => r.id === row.id);
      return updated ? createUpdatedRowData(row, updated) : row;
    });

  // Helper: Map rows with regular edits including constValues
  const applyRegularEditsWithConstValues = (dataArray, regularEdits) =>
    dataArray.map((row) => {
      const updated = regularEdits.find((r) => r.id === row.id);
      if (!updated) return row;
      return {
        ...createUpdatedRowData(row, updated),
        constValues: {
          ...row.constValues,
          Cascade: updated["Cascade"],
          Line: updated["Line"],
          ...createDayFields(updated),
        },
      };
    });

  // Helper: Insert duplicate row after original in array
  const insertDuplicateAfterOriginal = (dataArray, dupRow, originalId) => {
    const index = dataArray.findIndex((row) => row.id === originalId);
    if (index === -1) return dataArray;
    if (dataArray.some((r) => r.id === dupRow.id)) return dataArray;
    return [
      ...dataArray.slice(0, index + 1),
      dupRow,
      ...dataArray.slice(index + 1),
    ];
  };

  const getOriginalIdFromDuplicate = (dupRow) => {
    if (dupRow.id.includes("DUP_")) {
      return dupRow.id.split("_").slice(2).join("_");
    }
    return dupRow.id;
  };

  const insertDuplicateRowInState = (dupRow) => {
    const originalId = getOriginalIdFromDuplicate(dupRow);
    setDuplicateRows((prev) => appendRowIfMissing(prev, dupRow));
    setEditableTableData((prev) =>
      insertDuplicateAfterOriginal(prev, dupRow, originalId),
    );
    setFilteredEditableTableData((prev) =>
      insertDuplicateAfterOriginal(prev, dupRow, originalId),
    );
    setData((prev) => insertDuplicateAfterOriginal(prev, dupRow, originalId));
  };

  // Helper: Process duplicate rows from API
  const processDuplicateRowsFromAPI = (duplicateRowsFromAPI) => {
    duplicateRowsFromAPI.forEach(insertDuplicateRowInState);
  };

  // Helper: Apply regular edits to all data states
  const applyRegularEditsToAllStates = (regularEdits) => {
    setEditableTableData((prev) =>
      applyRegularEditsWithConstValues(prev, regularEdits),
    );
    setFilteredEditableTableData((prev) =>
      applyRegularEditsToRows(prev, regularEdits),
    );
    setData((prev) => applyRegularEditsToRows(prev, regularEdits));
  };

  const updateAddedRowWithEdit = (addedRow, editedAddedRows) => {
    const updatedRow = editedAddedRows.find(
      (editRow) => editRow.id === addedRow.id,
    );
    if (!updatedRow) return addedRow;
    return {
      ...addedRow,
      Line: updatedRow["Line"],
      Cascade: updatedRow["Cascade"],
      ...createDayFields(updatedRow),
      "Reason for change": updatedRow["Reason for change"],
      isUpdated: true,
      constValues: {
        ...addedRow.constValues,
        Cascade: updatedRow["Cascade"],
        Line: updatedRow["Line"],
        Mon: updatedRow.constValues?.["Mon"] || updatedRow["Mon"],
        Tue: updatedRow.constValues?.["Tue"] || updatedRow["Tue"],
        Wed: updatedRow.constValues?.["Wed"] || updatedRow["Wed"],
        Thu: updatedRow.constValues?.["Thu"] || updatedRow["Thu"],
        Fri: updatedRow.constValues?.["Fri"] || updatedRow["Fri"],
        Sat: updatedRow.constValues?.["Sat"] || updatedRow["Sat"],
        Sun: updatedRow.constValues?.["Sun"] || updatedRow["Sun"],
      },
    };
  };

  const applyAddedRowEdits = (rows, editedAddedRows) => {
    return rows.map((addedRow) =>
      updateAddedRowWithEdit(addedRow, editedAddedRows),
    );
  };

  // Helper: Update addedRows with edited data
  const updateAddedRowsWithEdits = (editedAddedRows) => {
    setAddedRows((prev) => applyAddedRowEdits(prev, editedAddedRows));
  };

  // Helper: Process main table edits (regular + duplicates)
  const processMainTableEdits = (editedMainRows) => {
    const duplicateRowsFromAPI = editedMainRows.filter(
      (row) => row.IsDuplicateRow === true,
    );
    const regularEdits = editedMainRows.filter((row) => !row.IsDuplicateRow);

    if (regularEdits.length > 0) {
      applyRegularEditsToAllStates(regularEdits);
    }
    if (duplicateRowsFromAPI.length > 0) {
      processDuplicateRowsFromAPI(duplicateRowsFromAPI);
    }
  };

  // Helper: Assign priority to new rows based on existing data
  const assignPriorityToNewRows = (newRows, existingData) => {
    const existingPriorityMap = new Map();
    existingData.forEach((row) => {
      const key = `${row["CBU code"]}_${row["Site code"]}`;
      if (row["Priority"] && !existingPriorityMap.has(key)) {
        existingPriorityMap.set(key, row["Priority"]);
      }
    });

    const newRowsPriorityMap = new Map();
    newRows.forEach((item) => {
      const key = `${item["CBU code"]}_${item["Site code"]}`;
      if (existingPriorityMap.has(key)) {
        item["Priority"] = existingPriorityMap.get(key);
      } else if (newRowsPriorityMap.has(key)) {
        item["Priority"] = newRowsPriorityMap.get(key);
      } else {
        newRowsPriorityMap.set(key, item["Priority"]);
      }
    });
  };

  // Helper: Add new rows to addedRows state
  const addNewRowsToState = (newRows) => {
    setAddedRows((prev) => {
      const timestamp = Date.now();
      const updatedData = newRows.map((item, index) => ({
        ...item,
        IsAddNewCBU: true,
        constValues: {
          Cascade: item["Cascade"],
          Line: item["Line"],
          ...createDayFields(item),
          "Reason for change": item["Reason for change"],
          Priority: item["Priority"],
        },
        id: `NEW_${timestamp}_${prev.length + index}`,
      }));
      return [...prev, ...updatedData];
    });
  };

  const handleFileUpload = (event) => {
    setLoading(true);
    const file = event.target.files[0];
    if (!file) return;
    try {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        setSnackBarObject({
          snackbarOpen: true,
          message: "Please upload Excel files.",
          type: "error",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const jsonData = await readExcelFileToJson(file);
          const newFilteredRows = jsonData.filter(isNewRow);
          const editCandidates = jsonData.filter(isEditCandidate);

          const condSatisfied = validateExportData([
            ...newFilteredRows,
            ...editCandidates,
          ]);
          if (!condSatisfied) return;

          const changedRowsDaysOnly = getChangedRowsDaysOnly(
            editCandidates,
            filteredEditableTableData,
          );
          const lineCascadeChangedRows = getChangedRowsLineCascade(
            editCandidates,
            filteredEditableTableData,
          );

          const editedRowsPayload = [
            ...changedRowsDaysOnly,
            ...lineCascadeChangedRows,
          ].map((item) => ({ ...item, AddNewCBU: 1 }));
          const newRowsPayload = newFilteredRows.map((item) => ({
            ...item,
            Week: filteredEditableTableData[0]?.["Week"],
            Flag: filteredEditableTableData[0]?.["Flag"],
            AddNewCBU: 1,
            Status: "Addition of a new CBU",
          }));

          const payload = {
            table_data: [...editedRowsPayload, ...newRowsPayload],
          };
          if (payload.table_data.length === 0) {
            setSnackBarObject({
              snackbarOpen: true,
              message: "No changes detected in the uploaded file",
              type: "error",
            });
            return;
          }

          const newData = await getValidatedNewCBUFromExcel(payload);
          if (newData?.validation_error) {
            const reason = newData.validation_error.errors[0];
            if (reason.includes("Found duplicate combinations")) {
              const fetchReason = reason
                .split(": (")[1]
                .split("),")
                .map((item) => item.replace(/[()]/g, "").trim());
              setDuplicateEntryPopup({ value: true, message: fetchReason });
            } else {
              setSnackBarObject({
                snackbarOpen: true,
                message: reason,
                type: "error",
              });
            }
            return;
          }

          const validatedData = newData["validated_data"];
          const newRows = validatedData.filter(
            (item) => !("id" in item) || !item.id || item.id === "",
          );
          const editedRows = validatedData.filter(
            (item) => "id" in item && item.id && item.id !== "",
          );

          if (newRows.length > 0) {
            assignPriorityToNewRows(newRows, filteredEditableTableData);
            addNewRowsToState(newRows);
          }

          if (editedRows.length > 0) {
            const editedAddedRows = editedRows.filter(
              (row) => typeof row.id === "string" && row.id.startsWith("NEW_"),
            );
            const editedMainRows = editedRows.filter(
              (row) =>
                !(typeof row.id === "string" && row.id.startsWith("NEW_")),
            );

            if (editedAddedRows.length > 0)
              updateAddedRowsWithEdits(editedAddedRows);
            if (editedMainRows.length > 0)
              processMainTableEdits(editedMainRows);
          }

          setSnackBarObject({
            snackbarOpen: true,
            message: `Excel file uploaded successfully! ${newRows.length} new row(s) added, ${editedRows.length} row(s) updated.`,
            type: "success",
          });
        } finally {
          setLoading(false);
          event.target.value = "";
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.log("File upload error:", error);
      setSnackBarObject({
        snackbarOpen: true,
        message: error.message,
        type: "error",
      });
    }
  };

  // to add duplicate row
  const addDuplicateRow = (id, row, IsAddNewCBU) => {
    if (IsAddNewCBU) {
      const array = [...addedRows];
      const timestamp = Date.now();
      // Deep clone the row to avoid reference issues
      const duplicate = structuredClone({
        ...row,
        id: `NEW_${timestamp}_DUP_${array.length}`,
        IsDuplicateRow: true,
        IsAddNewCBU: 1,
        isSubmitted: 0,
        Status: "Addition of a new row",
      });
      array.push(duplicate);
      setAddedRows(array);
      setDuplicateRows((prev) => [...prev, duplicate]);
    } else {
      // Find the row in the current data (with latest values)
      const dataIndex = data.findIndex((item) => item.id === id);
      if (dataIndex === -1) return;

      const newRow = data[dataIndex];

      // Create duplicate with new unique id
      const timestamp = Date.now();
      // Deep clone to ensure complete independence from original row
      const duplicate = structuredClone({
        ...newRow,
        id: `DUP_${timestamp}_${id}`,
        IsDuplicateRow: 1, // Only the duplicate should have this flag
        isUpdated: false, // Reset isUpdated flag for the duplicate
        isAdded: false, // Reset isAdded flag
        PreCascade: newRow.Cascade,
        PreLine: newRow.Line,
        // Copy cascadeOptions and lineOptions for dropdowns
        cascadeOptions: newRow.cascadeOptions || [],
        lineOptions: newRow.lineOptions || [],
        // Store current values as constValues (not the old constValues)
        constValues: {
          Cascade: newRow.Cascade,
          Line: newRow.Line,
          Mon: newRow.Mon,
          Tue: newRow.Tue,
          Wed: newRow.Wed,
          Thu: newRow.Thu,
          Fri: newRow.Fri,
          Sat: newRow.Sat,
          Sun: newRow.Sun,
          Priority: newRow.Priority,
          "Reason for change": newRow["Reason for change"],
          Status: "Addition of a new row",
        },
        isSubmitted: false,
      });

      // Update filteredEditableTableData by inserting duplicate right after original row
      setFilteredEditableTableData((prev) => {
        const index = prev.findIndex((item) => item.id === id);
        if (index === -1) return prev;

        return [
          ...prev.slice(0, index + 1),
          duplicate,
          ...prev.slice(index + 1),
        ];
      });

      // Update editableTableData similarly
      setEditableTableData((prev) => {
        const index = prev.findIndex((item) => item.id === id);
        if (index === -1) return prev;

        return [
          ...prev.slice(0, index + 1),
          duplicate,
          ...prev.slice(index + 1),
        ];
      });

      // Update the visible data array
      setData((prev) => {
        const index = prev.findIndex((item) => item.id === id);
        if (index === -1) return prev;

        return [
          ...prev.slice(0, index + 1),
          duplicate,
          ...prev.slice(index + 1),
        ];
      });

      setDuplicateRows((prev) => [...prev, duplicate]);
    }
    setSnackBarObject({
      snackbarOpen: true,
      message: "Duplicate row added successfully.",
      type: "success",
    });
    setBtnDisable(true);
  };

  return (
    <Fragment>
      <Fragment>
        {/* cascade and line fitlers for 2nd table along with reset filter & add new CBU */}
        <div className={styles.menuPanel}>
          <div className={styles.filters} style={{ width: "25%" }}>
            {Array.isArray(editableTableFilters) &&
              editableTableFilters.length > 0 &&
              editableTableFilters
                .filter((filter) => filter.isFavourite)
                .map((filter) => (
                  <ListItem
                    key={filter.label}
                    className={styles.listCustomClass}
                  >
                    <MultiSelectWithSearchInput
                      key={`${filter.label}`}
                      label={filter.label}
                      options={getFilterOptions(filter)}
                      filterValues={filter.selectedData}
                      handleFilter={(label, values) =>
                        handleFilterChange(label, values)
                      }
                      disabled={false}
                    />
                  </ListItem>
                ))}
          </div>
          <div
            style={{
              width: "75%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: "2px",
            }}
          >
            <div
              style={{ paddingLeft: 5, display: "flex", alignItems: "center" }}
            >
              <button
                type="button"
                onClick={resetFilters}
                aria-label="Reset Filters"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  cursor: "pointer",
                }}
              >
                <img
                  src={resetBtn}
                  alt="Reset Filters"
                  style={{ cursor: "pointer" }}
                />
              </button>
            </div>
            {editableTableData.length > 0 && (
              <div
                style={{
                  paddingLeft: "5px",
                  display: "flex",
                  alignItems: "center",
                  columnGap: "16px",
                }}
              >
                <CustomButton
                  icon={Excel}
                  label="Export"
                  type="mm-save"
                  onClick={handleExportToExcel}
                />
                <CustomButton
                  disabled={hasS1Flag([...editableTableData])}
                  icon={Upload}
                  label="Upload"
                  type="file_upload"
                  onClick={(file) => {
                    handleFileUpload(file);
                  }}
                />
                <CustomButton
                  disabled={hasS1Flag([...editableTableData])}
                  icon={Plus}
                  label="Add new CBU"
                  type="mm-save"
                  onClick={addNewCBUDrawerToggle}
                />
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <div className={styles.blurBackground}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer
            className={styles.table}
            sx={{ flex: 1, overflow: "auto" }}
            ref={containerRef}
          >
            <Table size="small">
              <TblHeader
                columns={editableTableDataColumns}
                requestSort={requestSort}
                sortConfig={sortConfig}
                isActionable
              />
              <TblBody
                columns={editableTableDataColumns}
                body={[
                  ...filteredAddedRows,
                  ...(editableTableData.length > 0 &&
                  editableTableData[0]["Flag"] === "M2"
                    ? filteredDuplicateRows
                    : []),
                  ...sortedData,
                ]}
                isActionable
                // preHandleInputChange={preHandleInputChange}
                preHandleInputChange={() => {}}
                handleInputChange={handleInputChange}
                rowResetHandler={rowResetHandler}
                rowDeleteHandler={rowDeleteHandler}
                addDuplicateRow={addDuplicateRow}
              />
              <TblFooter
                columns={editableTableDataColumns}
                footerColums={[
                  "Mon",
                  "Tue",
                  "Wed",
                  "Thu",
                  "Fri",
                  "Sat",
                  "Sun",
                  "Total",
                ]}
                exeColms={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]}
                data={[
                  ...filteredAddedRows,
                  ...(editableTableData.length > 0 &&
                  editableTableData[0]["Flag"] === "M2"
                    ? filteredDuplicateRows
                    : []),
                  ...editableTableFooter,
                ]}
                isActionable={true}
              />
            </Table>
          </TableContainer>
        )}

        {/* reason for change drawer component */}
        {drawerState.isOpen && (
          <ReasonForChange
            anchor="right"
            earlierSavedComments={saveComments}
            isDrawerOpen={drawerState}
            saveCommentinResponseChange={saveCommentInResponseChange}
            cancelCommentinResponseChange={cancelCommentInResponseChange}
          />
        )}
        {/* add new CBU drawer component */}
        {isDrawerOpen && (
          <AddNewCBU
            anchor="right"
            drawerIsOpen={isDrawerOpen}
            dataFields={
              editableTableData.length ? Object.keys(editableTableData[0]) : []
            }
            currentData={editableTableData}
            filteredData={globalFilters}
            drawerToggleHandler={addNewCBUDrawerToggle}
            onAddDataHandler={(newRows) => addNewCBUHandler(newRows)}
          />
        )}
      </Fragment>
      {/* snackbar component */}
      {snackBarObject.snackbarOpen && (
        <SnackBarMessage
          type={snackBarObject.type}
          snackbarMessage={snackBarObject.message}
          snackbarOpen={snackBarObject.snackbarOpen}
          setSnackbarOpen={closeSnackbar}
        />
      )}

      {duplicateEntryPopup && (
        <DuplicateRowPopup
          duplicateMessage={duplicateEntryPopup.message}
          popupOpen={duplicateEntryPopup.value}
          handlePopupOpen={() => {
            setDuplicateEntryPopup({ value: false, message: [] });
          }}
        />
      )}
    </Fragment>
  );
};

MMInputView.propTypes = {
  username: PropTypes.string.isRequired,
  exportToExcel: PropTypes.func.isRequired,
};

export default React.memo(MMInputView);
